import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ActionButtons,
  Badge,
  Button,
  DataTable,
  EmptyState,
  SearchWithFilter,
  type DataTableColumn,
  type FilterSelection,
} from '@enamelai/design-system'
import { ReactComponent as CloudStorageIcon } from '@assets/images/Settings/cloud-storage.svg'
import { useTheme } from '@context/ThemeContext'
import { useCloudStorage } from '@hooks/useCloudStorage'
import { useDebouncedValue } from '@hooks/useDebouncedValue'
import { useAppSelector } from '@store/hooks'
import { useSettingsSubpageLoading } from '@pages/Settings/SettingsSubpageGate.component'
import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import { formatFileSize } from '@services/api/cloudStorage/cloud-storage.api'
import { maskString } from '@utils/mask-string'
import {
  sortRows,
  usePagedRows,
  useTableSort,
} from '../../shared/settingsTable'
import {
  CLOUD_STORAGE_ACTIONS_ENABLED,
  CLOUD_STORAGE_CATEGORIES,
  CLOUD_STORAGE_EDIT_INFO_ENABLED,
  CLOUD_STORAGE_FILTER_LABELS,
  CLOUD_STORAGE_PLAN_DETAILS_ENABLED,
  CLOUD_STORAGE_SOURCE_BADGE_COLORS,
  CLOUD_STORAGE_STATUS_BADGES,
  CLOUD_STORAGE_STATUS_ENABLED,
  CloudStorageFile,
  NO_PATIENT_LABEL,
} from './cloudStorageSettings.constants'
import './CloudStorageStyle.css'

const categoryIdByLabel = new Map(
  CLOUD_STORAGE_CATEGORIES.map((category) => [category.label, category.id]),
)

/**
 * Fetch one page from the server (search applied server-side) and let the
 * table page client-side over the result. Category/type/source are
 * multi-select and are refined client-side, matching the §7 phase-1 approach.
 *
 * Must stay within the API's `pageSize` cap of 100 — requesting more is
 * rejected with a 400.
 */
const CLOUD_STORAGE_FETCH_SIZE = 100

const SEARCH_DEBOUNCE_MS = 300

/**
 * Beyond this horizon the capacity projection is noise (a near-zero growth rate
 * produces absurd figures), so the copy degrades to a qualitative statement.
 */
const MAX_PROJECTION_MONTHS = 600

/** Phrases the time-to-capacity estimate, collapsing implausible horizons. */
const renderCapacityProjection = (months: number | null): string => {
  if (months === null || months <= 0) {
    return "capacity isn't a concern yet."
  }
  if (months > MAX_PROJECTION_MONTHS) {
    return "you won't reach capacity for many years."
  }
  if (months >= 24) {
    return `you'll reach capacity in about ${Math.round(months / 12)} years.`
  }
  return `you'll reach capacity in about ${Math.round(months)} months.`
}

/** Masks each part of a patient's name for HIPAA view, leaving placeholders as-is. */
const maskPatientName = (name: string): string =>
  name
    .split(' ')
    .map((part) => (part === NO_PATIENT_LABEL ? part : maskString(part)))
    .join(' ')

const readFileSortValue = (row: CloudStorageFile, columnId: string): string => {
  if (columnId === 'uploadedAt') {
    return String(row.uploadedAtTime ?? 0).padStart(15, '0')
  }
  if (columnId === 'fileSize') {
    return String(row.fileSizeBytes).padStart(16, '0')
  }
  if (columnId === 'status') {
    return row.status
      ? CLOUD_STORAGE_STATUS_BADGES[row.status].label.toLowerCase()
      : ''
  }
  return String(row[columnId as keyof CloudStorageFile] ?? '').toLowerCase()
}

const CloudStorage = (): React.ReactElement => {
  const { setHeaderButtons } = useSettingsHeader()
  const { resolvedTheme } = useTheme()
  const badgeMode = resolvedTheme === 'dark' ? 'solid' : 'tinted'

  const [searchQuery, setSearchQuery] = useState('')
  const [searchFilters, setSearchFilters] = useState<FilterSelection>({})
  const [fileSort, toggleFileSort] = useTableSort()
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const activeClinicId = selectedClinic?.clinic_id
  const isHIPAAView = useAppSelector((state) => state.ui.isHIPAAView)

  const debouncedSearch = useDebouncedValue(
    searchQuery.trim(),
    SEARCH_DEBOUNCE_MS,
  )

  const {
    summaryLoading,
    summaryError,
    plan,
    categories,
    filesLoading,
    filesError,
    files,
  } = useCloudStorage({
    search: debouncedSearch || undefined,
    clinicId: activeClinicId,
    page: 1,
    pageSize: CLOUD_STORAGE_FETCH_SIZE,
  })
  const [dataReady, setDataReady] = useState(!activeClinicId)
  useSettingsSubpageLoading(Boolean(activeClinicId) && !dataReady)

  useEffect(() => {
    if (!activeClinicId) {
      setDataReady(true)
      return
    }
    setDataReady(false)
  }, [activeClinicId])

  useEffect(() => {
    if (!activeClinicId) return
    if (summaryLoading || filesLoading) return
    setDataReady(true)
  }, [activeClinicId, filesLoading, summaryLoading])

  useEffect(() => {
    setHeaderButtons(
      CLOUD_STORAGE_EDIT_INFO_ENABLED ? (
        <Button
          className="settings-ds-button"
          variant="primary"
          size="md"
          type="button"
        >
          Edit info
        </Button>
      ) : null,
    )
    return () => setHeaderButtons(null)
  }, [setHeaderButtons])

  const clearAllFilters = useCallback((): void => {
    setSearchFilters({})
    setSearchQuery('')
  }, [])

  // Type/source options come from the live file list; category labels stay in the UI.
  const filterOptions = useMemo<Record<string, string[]>>(
    () => ({
      category: CLOUD_STORAGE_CATEGORIES.map((category) => category.label),
      type: Array.from(new Set(files.map((file) => file.type)))
        .filter(Boolean)
        .sort(),
      source: Array.from(new Set(files.map((file) => file.sourceLabel)))
        .filter(Boolean)
        .sort(),
    }),
    [files],
  )

  const hasSearchOrFilter =
    searchQuery.trim().length > 0 ||
    Object.values(searchFilters).some((values) => (values?.length ?? 0) > 0)

  const filteredFiles = useMemo(() => {
    const selectedCategoryIds = new Set(
      (searchFilters.category ?? [])
        .map((label) => categoryIdByLabel.get(label))
        .filter((id): id is CloudStorageFile['categoryId'] => Boolean(id)),
    )
    const selectedTypes = new Set(searchFilters.type ?? [])
    const selectedSources = new Set(searchFilters.source ?? [])

    return files.filter((file) => {
      if (
        selectedCategoryIds.size &&
        !selectedCategoryIds.has(file.categoryId)
      ) {
        return false
      }
      if (selectedTypes.size && !selectedTypes.has(file.type)) {
        return false
      }
      if (selectedSources.size && !selectedSources.has(file.sourceLabel)) {
        return false
      }
      return true
    })
  }, [files, searchFilters])

  const sortedFiles = useMemo(
    () => sortRows(filteredFiles, fileSort, readFileSortValue),
    [filteredFiles, fileSort],
  )
  const filesPage = usePagedRows(sortedFiles)

  const columns: DataTableColumn<CloudStorageFile>[] = useMemo(
    () => [
      { id: 'fileName', label: 'File name', sortable: true },
      {
        id: 'sourceLabel',
        label: 'Source',
        sortable: true,
        renderCell: (row) => (
          <Badge
            mode={badgeMode}
            color={CLOUD_STORAGE_SOURCE_BADGE_COLORS[row.source] ?? 'gray'}
            label={row.sourceLabel}
          />
        ),
      },
      {
        id: 'patient',
        label: 'Patient',
        sortable: true,
        renderCell: (row) =>
          isHIPAAView ? maskPatientName(row.patient) : row.patient,
      },
      { id: 'uploadedAt', label: 'Date', sortable: true },
      { id: 'fileSize', label: 'Size', sortable: true },
      { id: 'uploadedBy', label: 'Uploaded by', sortable: true },
      ...(CLOUD_STORAGE_STATUS_ENABLED
        ? [
            {
              id: 'status',
              label: 'Status',
              sortable: true,
              renderCell: (row: CloudStorageFile) => {
                if (!row.status) return '—'
                const badge = CLOUD_STORAGE_STATUS_BADGES[row.status]
                return (
                  <Badge
                    mode={badgeMode}
                    color={badge.color}
                    label={badge.label}
                  />
                )
              },
            },
          ]
        : []),
    ],
    [badgeMode, isHIPAAView],
  )

  // Segment widths are computed from raw bytes: rounded GB collapses to 0 for
  // small tenants and would render an empty bar.
  const segmentWidths = useMemo(() => {
    const usedTotal = plan?.usedBytes ?? 0
    if (usedTotal <= 0) return []
    return categories.map((category) => ({
      id: category.id,
      colorVar: category.colorVar,
      widthPercent: (category.usedBytes / usedTotal) * 100,
    }))
  }, [categories, plan])

  const renderPlanCard = (): React.ReactElement => {
    if (summaryLoading || summaryError || !plan) {
      return (
        <section className="settings-cloud-storage-plan-card">
          <p className="settings-cloud-storage-note">
            {summaryLoading
              ? 'Loading storage usage…'
              : (summaryError ?? 'Storage usage is unavailable right now.')}
          </p>
        </section>
      )
    }

    const usedPercentOfPlan =
      plan.totalBytes > 0
        ? Math.min(100, (plan.usedBytes / plan.totalBytes) * 100)
        : 0
    const legendCategories =
      categories.length > 0 ? categories : CLOUD_STORAGE_CATEGORIES

    return (
      <section className="settings-cloud-storage-plan-card">
        <div className="settings-cloud-storage-plan-identity">
          <span className="settings-cloud-storage-plan-icon">
            <CloudStorageIcon aria-hidden="true" />
          </span>
          <div className="settings-cloud-storage-plan-text">
            <p className="settings-cloud-storage-plan-name">{plan.name}</p>
            <p className="settings-cloud-storage-plan-renewal">
              Renews {plan.renewsOn}
              {CLOUD_STORAGE_PLAN_DETAILS_ENABLED
                ? ` · $${plan.monthlyPrice}/month`
                : ''}
            </p>
          </div>
        </div>

        <p className="settings-cloud-storage-usage">
          <span className="settings-cloud-storage-usage-value">
            {formatFileSize(plan.usedBytes)}
          </span>
          <span className="settings-cloud-storage-usage-total">
            of {plan.totalGb} GB used
          </span>
        </p>

        <div
          className="settings-cloud-storage-segment-bar"
          role="img"
          aria-label={`${plan.usedPercent}% of storage used`}
        >
          <div
            className="settings-cloud-storage-segment-fill"
            style={{ width: `${usedPercentOfPlan}%` }}
          >
            {segmentWidths.map((segment) => (
              <div
                key={segment.id}
                className="settings-cloud-storage-segment"
                style={{
                  width: `${segment.widthPercent}%`,
                  background: `var(${segment.colorVar})`,
                }}
              />
            ))}
          </div>
        </div>

        {CLOUD_STORAGE_PLAN_DETAILS_ENABLED ? (
          <p className="settings-cloud-storage-note">
            {plan.usedPercent}% used - at current growth (~
            {formatFileSize(plan.growthRateBytesPerMonth)}/mo),{' '}
            {renderCapacityProjection(plan.monthsUntilCapacity)}
          </p>
        ) : null}

        <div className="settings-cloud-storage-legend">
          {legendCategories.map((category) => (
            <span
              key={category.id}
              className="settings-cloud-storage-legend-item"
            >
              <span
                className="settings-cloud-storage-legend-dot"
                style={{ background: `var(${category.legendColorVar})` }}
              />
              {category.label}
            </span>
          ))}
        </div>
      </section>
    )
  }

  if (!activeClinicId) {
    return (
      <EmptyState
        iconClass="mgc mgc_cloud_2_fill"
        title="Select a clinic"
        description="Choose a clinic to view its cloud storage."
      />
    )
  }

  const tableState = filesError
    ? 'error'
    : filesLoading && dataReady
      ? 'loading'
      : sortedFiles.length === 0
        ? 'empty'
        : 'default'

  return (
    <div className="settings-cloud-storage">
      {renderPlanCard()}

      <section className="settings-cloud-storage-breakdown">
        <div className="settings-cloud-storage-breakdown-header">
          <h3 className="settings-cloud-storage-breakdown-title">
            Storage breakdown
          </h3>
          <SearchWithFilter
            className="settings-cloud-storage-search"
            placeholder="Search"
            ariaLabel="Search files"
            value={searchQuery}
            onChange={setSearchQuery}
            filters={filterOptions}
            filterLabels={CLOUD_STORAGE_FILTER_LABELS}
            selectedFilters={searchFilters}
            onFilterChange={setSearchFilters}
            onReset={clearAllFilters}
          />
        </div>

        {dataReady ? (
          <DataTable<CloudStorageFile>
            className="settings-cloud-storage-table"
            columns={columns}
            rows={filesPage.pageRows}
            state={tableState}
            emptyState={{
              title: 'No files found',
              description: hasSearchOrFilter
                ? 'No files match your search.'
                : "You don't have any files on record yet.",
            }}
            errorState={{
              title: 'Files unavailable',
              description: filesError ?? undefined,
            }}
            sortColumnId={fileSort.columnId}
            sortDirection={fileSort.columnId ? fileSort.direction : undefined}
            onSort={toggleFileSort}
            pagination={filesPage.pagination}
            showActions={CLOUD_STORAGE_ACTIONS_ENABLED}
            actionsLabel="Action/s"
            renderActions={
              CLOUD_STORAGE_ACTIONS_ENABLED
                ? () => <ActionButtons showEdit={false} />
                : undefined
            }
          />
        ) : null}
      </section>
    </div>
  )
}

export default CloudStorage
